import { createRootRouteWithContext, Outlet, useMatches, useRouter } from "@tanstack/react-router";
import { HIGHARC_DOMAIN } from "common/constants/env";
import { SITE_DESCRIPTION } from "common/constants/marketing";
import { reloadPage, shouldAttemptChunkReload } from "common/utils/chunkReloadGuard";
import { initPosthog, posthog } from "lib/analytics";
import { Suspense, useEffect, useRef } from "react";
import { Helmet, HelmetProvider } from "react-helmet-async";
import "ui/styles/global/index.scss";
import AppContainer from "../components/AppContainer";
import { hasDeploymentChanged, startDeploymentPolling } from "../src/deploymentRefresh";
import type { AppRouterContext } from "../src/main";

// Throttled build ID check — returns true if the live deployment differs from
// the build currently running in the browser.
let lastVersionCheckTime = 0;
const VERSION_CHECK_INTERVAL = 30_000; // 30 seconds

async function hasNewDeployment(): Promise<boolean> {
  const now = Date.now();
  if (now - lastVersionCheckTime < VERSION_CHECK_INTERVAL) {
    return false;
  }
  lastVersionCheckTime = now;

  const clientBuildId = process.env.PUBLIC_BUILD_ID;
  if (!clientBuildId) {
    return false;
  }

  try {
    const res = await fetch("/version.json", { cache: "no-store" });
    if (!res.ok) {
      return false;
    }
    const data = await res.json();
    return hasDeploymentChanged(clientBuildId, data?.buildId);
  } catch {
    return false;
  }
}

export const Route = createRootRouteWithContext<AppRouterContext>()({
  // Check for new deployments before loading child route chunks
  beforeLoad: async ({ location }) => {
    if ((await hasNewDeployment()) && shouldAttemptChunkReload()) {
      window.location.href = location.href;
      // Hang so the router doesn't continue loading stale chunks while the reload happens
      await new Promise<never>(() => {});
    }
  },
  component: RootLayout,
});

function RootLayout() {
  const router = useRouter();
  const matches = useMatches();
  const isPublicPage = matches.some((match) => match.staticData?.isPublicPage === true);
  const posthogInitialized = useRef(false);

  // Initialize PostHog and track page views
  useEffect(() => {
    if (!posthogInitialized.current) {
      posthogInitialized.current = true;
      void initPosthog();
    }

    const unsubscribe = router.subscribe("onResolved", () => {
      posthog.capture("$pageview");
    });

    return unsubscribe;
  }, [router]);

  // Poll for new deployments when the user is idle (not navigating)
  useEffect(() => {
    if (!process.env.PUBLIC_BUILD_ID) {
      return;
    }

    // best-effort immediate check: this may no-op if beforeLoad just updated throttle state
    const stopPolling = startDeploymentPolling({
      checkForNewDeployment: hasNewDeployment,
      onNewDeployment: () => {
        if (shouldAttemptChunkReload()) {
          reloadPage();
        }
      },
    });

    // Browsers throttle setInterval in backgrounded tabs and pause it entirely
    // while the laptop is asleep, so the 5-min poll can miss overnight deploys.
    // Force a check the moment the tab becomes visible again.
    const onVisibilityChange = async () => {
      if (document.visibilityState !== "visible") {
        return;
      }
      lastVersionCheckTime = 0;
      if ((await hasNewDeployment()) && shouldAttemptChunkReload()) {
        reloadPage();
      }
    };
    document.addEventListener("visibilitychange", onVisibilityChange);

    return () => {
      stopPolling();
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, []);

  return (
    <HelmetProvider>
      <Helmet>
        <meta name="description" content={SITE_DESCRIPTION} />
        <meta property="og:title" content="Higharc" />
        <meta property="og:description" content={SITE_DESCRIPTION} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={HIGHARC_DOMAIN} />
        <meta property="og:image" content="/static/og-image.png" />
        <meta property="twitter:image" content="/static/og-image.png" />
      </Helmet>
      <AppContainer isPublicPage={isPublicPage}>
        <Suspense>
          <Outlet />
        </Suspense>
      </AppContainer>
    </HelmetProvider>
  );
}
